import type {
  CodeToolSecretListing,
  CodeToolSecretsWrite,
} from "@repo/app-types/entities/tools/code";
import type { CustomToolParameter } from "@repo/app-types/entities/tools/custom";
import isEqual from "lodash-es/isEqual.js";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { FieldView } from "../../../components/diffableField.js";
import type { ToolFormConfig } from "../formConfigs/baseFormConfig.js";
import type { CodeToolTestInputFormSettings } from "../formConfigs/codeToolTestInput.js";

export interface BaseToolFormData<TSettings> {
  name: string;
  description: string;
  settings: TSettings;
}

/** The settings a save sends, and a code tool's secrets write beside them. */
export interface PreparedToolSettings<TSettings> {
  settings: TSettings;
  secrets?: CodeToolSecretsWrite;
}

/** Changes to merge into the form, each field present only when it changes. */
export interface ToolFormChanges<TSettings> {
  name?: string;
  description?: string;
  settings?: Partial<TSettings>;
}

/** What a save sends. */
export interface BaseToolFormSaveData<
  TSettings,
> extends PreparedToolSettings<TSettings> {
  name: string;
  description: string;
}

interface UseToolFormParams<TSettings, TFormSettings> {
  config: ToolFormConfig<TSettings, TFormSettings>;
  initialData: BaseToolFormData<TSettings> | undefined;
  /** A code tool's stored secrets; must keep its identity between renders. */
  initialSecrets: readonly CodeToolSecretListing[];
  onSave: (data: BaseToolFormSaveData<TSettings>) => Promise<void>;
}

export interface UseToolFormResult<TSettings> {
  isEditMode: boolean;
  isFormValid: boolean;
  hasChanges: boolean;
  isSaving: boolean;
  onSave: () => Promise<void>;
  onDiscard: () => void;
  /**
   * `view` controls the view of the form's text fields. When set, their view
   * toggles don't render.
   */
  renderForm: (view?: FieldView) => ReactNode;
  /**
   * The settings and secrets write as the form has them now, unsaved, or
   * `undefined` after showing the form's errors when they aren't valid yet.
   */
  prepareSettings: () => PreparedToolSettings<TSettings> | undefined;
  /**
   * Shows an API refusal on the fields it is about, as a failed save does.
   * Returns whether any field took it.
   */
  applySaveError: (error: unknown) => boolean;
  /** The parameters as the form has them now, for a tool that has them. */
  parameters: readonly CustomToolParameter[] | undefined;
  /** A code tool's test input as the form has it now. */
  testInput: CodeToolTestInputFormSettings | undefined;
  /** Changes the test input, as an edit to the form. */
  onTestInputChange: (change: Partial<CodeToolTestInputFormSettings>) => void;
  /**
   * Merges changes into the unsaved ones, over the user's own, as if they had
   * made the edit. Settings go in only for a type whose config reads them.
   */
  mergeChanges: (changes: ToolFormChanges<TSettings>) => void;
  /** How many times a save or a discard has cleared the unsaved changes. */
  resetCount: number;
}

export function useBaseToolForm<TSettings, TFormSettings>(
  params: UseToolFormParams<TSettings, TFormSettings>,
): UseToolFormResult<TSettings> {
  const { config, initialData, initialSecrets, onSave } = params;

  // Initial form values: original tool values from props
  const initialName = initialData?.name ?? "";
  const initialDescription = initialData?.description ?? "";
  const initialSettings = useMemo(
    () => config.initialize(initialData?.settings, initialSecrets),
    [config, initialData?.settings, initialSecrets],
  );

  // Changed form values: changes the user made compared to the original
  const [changedName, setChangedName] = useState<string | undefined>(undefined);
  const [changedDescription, setChangedDescription] = useState<
    string | undefined
  >(undefined);
  const [changedSettings, setChangedSettings] = useState<
    Partial<TFormSettings>
  >({});

  const [resetCount, setResetCount] = useState(0);
  const discardChanges = useCallback(() => {
    setChangedName(undefined);
    setChangedDescription(undefined);
    setChangedSettings({});
    setResetCount((count) => count + 1);
  }, []);

  // Current form values: computed from initial + changed
  const name = changedName ?? initialName;
  const description = changedDescription ?? initialDescription;
  const settings = useMemo(
    () => ({
      ...initialSettings,
      ...changedSettings,
    }),
    [initialSettings, changedSettings],
  );

  // Sanitized form values: this is what we'll save.
  const sanitizedName = name.trim();
  const sanitizedDescription = description.trim();

  const isEditMode = Boolean(initialData);

  // Perform real-time validation that disables the save button
  const isFormValid =
    sanitizedName !== "" &&
    sanitizedDescription !== "" &&
    config.validate(settings);

  // Detect changes (only relevant in edit mode)
  const hasChanges = useMemo(() => {
    if (!isEditMode) {
      return false;
    }

    return (
      changedName !== undefined ||
      changedDescription !== undefined ||
      Object.keys(changedSettings).length !== 0
    );
  }, [isEditMode, changedName, changedDescription, changedSettings]);

  const handleSettingsChange = useCallback(
    (changedSettings: Partial<TFormSettings>) => {
      setChangedSettings((prev) => {
        const merged = { ...prev, ...changedSettings };
        return isEqual(prev, merged) ? prev : merged;
      });
    },
    [],
  );

  const handleMergeChanges = (changes: ToolFormChanges<TSettings>) => {
    if (changes.name !== undefined) {
      setChangedName(changes.name);
    }
    if (changes.description !== undefined) {
      setChangedDescription(changes.description);
    }
    const formSettings =
      changes.settings && config.readProposedSettings?.(changes.settings);
    if (formSettings) {
      handleSettingsChange(formSettings);
    }
  };

  const [isSaving, setIsSaving] = useState(false);

  const handlePrepareSettings = ():
    | PreparedToolSettings<TSettings>
    | undefined => {
    const result = config.save(settings);
    if (!result.isValid) {
      setChangedSettings((prev) => ({ ...prev, ...result.formSettings }));
      return undefined;
    }
    return { settings: result.settings, secrets: result.secrets };
  };

  const handleApplySaveError = (error: unknown): boolean => {
    const formSettings = config.readSaveError?.(error);
    if (!formSettings) return false;
    setChangedSettings((prev) => ({ ...prev, ...formSettings }));
    return true;
  };

  const parameters = config.readParameters?.(settings);
  const testInput = config.readTestInput?.(settings);

  const handleTestInputChange = (
    change: Partial<CodeToolTestInputFormSettings>,
  ) => {
    const formSettings = config.buildTestInputChange?.(change);
    if (formSettings) {
      handleSettingsChange(formSettings);
    }
  };

  const handleSave = async () => {
    if (!isFormValid) {
      return;
    }

    setIsSaving(true);
    try {
      // Perform just-in-time validation before saving, which may update the form with errors
      const prepared = handlePrepareSettings();
      if (!prepared) {
        return;
      }

      await onSave({
        name: sanitizedName,
        description: sanitizedDescription,
        ...prepared,
      });

      // Clear changed state after successful save
      discardChanges();
    } catch (error) {
      // `onSave` reports the failure itself; keep what the user typed so they
      // can fix it (a name that is already taken, say) and save again.
      handleApplySaveError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    discardChanges();
  };

  const renderForm = (view?: FieldView) => {
    const Form = config.Form;

    // Nothing is edited while a save is in flight: the save sends the form as
    // it stood, and a finished save resets the form, which would drop edits
    // made meanwhile. A disabled fieldset disables every native field in it.
    return (
      <fieldset disabled={isSaving} className="contents">
        <Form
          isEditMode={isEditMode}
          isReadOnly={isSaving}
          view={view}
          original={
            initialData && {
              name: initialName,
              description: initialDescription,
              settings: initialSettings,
            }
          }
          name={name}
          description={description}
          settings={settings}
          onNameChange={setChangedName}
          onDescriptionChange={setChangedDescription}
          onSettingsChange={handleSettingsChange}
        />
      </fieldset>
    );
  };

  return {
    isEditMode,
    isFormValid,
    hasChanges,
    isSaving,
    onSave: handleSave,
    onDiscard: handleDiscard,
    renderForm,
    prepareSettings: handlePrepareSettings,
    applySaveError: handleApplySaveError,
    parameters,
    testInput,
    onTestInputChange: handleTestInputChange,
    mergeChanges: handleMergeChanges,
    resetCount,
  };
}
